import { BOOKMAKERS } from './bookmakers';

/** Nginx hop slugs that must not match FullPrediction `/:slug`. */
export const AFFILIATE_HOP_SLUGS = [
  '1xmobi',
  '1xbet',
  '1xbet-app',
  '1xdownloadapp',
  'melbet',
  'melbetdownloadapp',
  'gopromomelbet',
  'betwinner',
  'betwinnerdownloadapp',
  'gopromobetwinner',
  'gopromo1xbet',
  'db-bet',
  'parimatch',
  'bet9ja',
  'betin',
];

const AFFILIATE_HOP_SLUG_SET = new Set(AFFILIATE_HOP_SLUGS);

export const isAffiliateHopSlug = (slug) =>
  Boolean(slug) && AFFILIATE_HOP_SLUG_SET.has(String(slug).toLowerCase());

const PLACEMENT_KEYS = [
  'promoHot',
  'recommended',
  'welcomeBanner',
  'predictionsPage',
  'matchOddsCta',
  'matchCompact',
  'accaSlip',
  'matchTipCta',
  'feedStrip',
  // Comparison table on /best-betting-sites only
  'hubCompare',
];

export const getBookmakerById = (id) =>
  BOOKMAKERS.find((b) => b.id === id) ?? null;

export const getBookmakerByName = (name) => {
  if (!name) return null;
  const key = String(name).toLowerCase().replace(/\s+/g, '');
  return (
    BOOKMAKERS.find((b) => {
      const byName = b.name.toLowerCase().replace(/\s+/g, '');
      return byName === key || b.id === key;
    }) ?? null
  );
};

const isOwnHostAffiliate = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(url, 'https://main-bet.com');
    return parsed.hostname === 'main-bet.com' || parsed.hostname === 'www.main-bet.com';
  } catch {
    return String(url).startsWith('/');
  }
};

const ownHostHopSlug = (url) => {
  if (!isOwnHostAffiliate(url)) return null;
  try {
    const parsed = new URL(url, 'https://main-bet.com');
    const slug = parsed.pathname.replace(/^\/+|\/+$/g, '').split('/')[0];
    return slug || null;
  } catch {
    return String(url).replace(/^\/+|\/+$/g, '').split('/')[0] || null;
  }
};

/** Hops nginx sends to the bonus list instead of a partner — not tracked as clicks. */
const RETIRED_HOP_SLUGS = new Set(['db-bet', 'parimatch', 'bet9ja', 'betin']);

const GO_LINK_RE = /\/api\/go\/([a-z0-9-]{2,40})\/?$/;

/**
 * Own-host affiliate hop → `/api/go/<hop>?src=&ch=site`: the backend logs the click
 * (partner, placement, channel, country) and redirects to the admin link or the same hop.
 * Live hops mirror backend/config/affiliateHops.js. A catalog href that is already an
 * admin short link (`/api/go/1win`) gets the same params. Any other href is returned as is.
 */
export const toTrackedHref = (href, source) => {
  const params = new URLSearchParams({ src: source || 'unknown', ch: 'site' });
  const goSlug = isOwnHostAffiliate(href) ? String(href).match(GO_LINK_RE)?.[1] : null;
  if (goSlug) return `/api/go/${goSlug}?${params}`;
  const slug = ownHostHopSlug(href)?.toLowerCase();
  if (!isAffiliateHopSlug(slug) || RETIRED_HOP_SLUGS.has(slug)) return href;
  return `/api/go/${slug}?${params}`;
};

/** True when CMS website is a hop we no longer send to a partner. */
export const isUnclaimableOffer = (bookmakerName, website) =>
  !resolveAffiliateHref(bookmakerName, website).href &&
  isAffiliateHopSlug(ownHostHopSlug(website));

const isHttpAffiliateUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Catalog affiliate URL wins; never fall back to a dead own-host hop (e.g. /parimatch). */
export const resolveAffiliateHref = (bookmakerName, website) => {
  const config = getBookmakerByName(bookmakerName);
  if (config?.links?.affiliate) {
    return { href: config.links.affiliate, config };
  }
  if (isAffiliateHopSlug(ownHostHopSlug(website))) {
    return { href: null, config };
  }
  if (config && isOwnHostAffiliate(website)) {
    return { href: null, config };
  }
  if (isHttpAffiliateUrl(website)) {
    return { href: website, config };
  }
  return { href: null, config };
};

/** Bonus list order: catalog `bonusTop` first, `trial` partners last (in catalog order), the rest keep the API order. */
export const sortTrialBookmakersLast = (bonuses) => {
  if (!Array.isArray(bonuses)) return [];
  const rank = (bonus) => {
    const config = getBookmakerByName(bonus?.bookmaker);
    if (config?.bonusTop) return -1;
    return config?.trial ? 1 + BOOKMAKERS.indexOf(config) : 0;
  };
  return bonuses
    .map((bonus, index) => ({ bonus, index, rank: rank(bonus) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ bonus }) => bonus);
};

export const getBookmakersByPlacement = (placement) => {
  if (!PLACEMENT_KEYS.includes(placement)) {
    return [];
  }

  return BOOKMAKERS.filter((b) => b.placements?.[placement])
    .sort((a, b) => (a.sort?.[placement] ?? 99) - (b.sort?.[placement] ?? 99));
};

/** Primary bookmaker for a placement (highest priority in sort). */
export const getBookmakerForPlacement = (placement) =>
  getBookmakersByPlacement(placement)[0] ?? null;

/** Rotates bookmakers daily for placements like matchCompact. */
export const getRotatedBookmakerForPlacement = (placement) => {
  const list = getBookmakersByPlacement(placement);
  if (!list.length) return null;
  const dayIndex = Math.floor(Date.now() / 86400000) % list.length;
  return list[dayIndex];
};

export const getWelcomeBannerBookmaker = () =>
  getBookmakersByPlacement('welcomeBanner')[0] ?? getBookmakerById('1xbet');

export const getPredictionBookmakerSlugs = () =>
  BOOKMAKERS.filter((b) => b.placements?.predictionsPage).map((b) => b.id);

export const getBookieDisplayName = (slug) => {
  const bookmaker = getBookmakerById(slug);
  return bookmaker?.name ?? null;
};

export const isValidPredictionBookmakerSlug = (slug) =>
  getPredictionBookmakerSlugs().includes(slug);

/** Maps The Odds API bookmaker keys to affiliate config ids (mirrors backend/config/oddsApi.js). */
const ODDS_API_KEY_TO_ID = {
  onexbet: '1xbet',
  pinnacle: 'pinnacle',
  betfair_ex_eu: 'betfair',
  polymarket: 'polymarket',
  kalshi: 'kalshi',
};

export const getBookmakerByOddsApiKey = (apiKey) => {
  const id = ODDS_API_KEY_TO_ID[apiKey];
  return id ? getBookmakerById(id) : null;
};
